/* eslint-disable import/no-cycle */
import * as React from 'react';
import { Messages } from '../../../gen-src/Localize';
import { PreventUserSelect } from '../../styles/PreventUserSelect';
import { TabStyles } from '../../styles/TabStyles';
import { TextEllipsis } from '../../styles/TextEllipsis';
import { conditional, makeTestId } from '../../utils/Declarative';
import { GlobalPointerEventCapturer } from '../../utils/GlobalPointerEventCapturer';
import { EventUtilsOptOut } from '../../utils/ReactHelpers';
import { getClientPosition } from '../../utils/UserInteraction';
import { DialogCloseButtonWidget } from '../Button/CloseButtonWidget';
import { DialogContentProps, DialogPositioningBehavior, DialogType } from './DialogWidget';

export const getStyles: () => {
  DraggableHeader: React.CSSProperties,
  Header: React.CSSProperties,
  TitleBar: React.CSSProperties,
  TitleBarWithCloseButton: React.CSSProperties,
  LightweightTitleBar: React.CSSProperties,
  LightweightTitleBarCloseButton: React.CSSProperties,
  Title: React.CSSProperties,
} = () => {
  return {
    DraggableHeader: {
      cursor: 'move',
    },

    Header: {
      ...PreventUserSelect,
      flexShrink: 0,
      minHeight: TabStyles.Sizing.BaseUnit * 5
    },

    TitleBar: {
      fontSize: TabStyles.Typography.Sizing.Default,
      color: TabStyles.Typography.Color.Primary,
      backgroundColor: 'var(--title-bar-background-color)',
      height: '100%',
      minHeight: 'inherit', // TFSID 732641
      paddingRight: TabStyles.Spacing.LargeSpace,
      paddingLeft: TabStyles.Spacing.LargeSpace,
      display: 'flex',
      alignItems: 'center',
      lineHeight: 'normal',
      ...TextEllipsis
    },

    TitleBarWithCloseButton: {
      paddingRight: 0,
    },

    LightweightTitleBar: {
      fontSize: 16,
      height: '100%',
      minHeight: 'inherit', // TFSID 732641
      paddingTop: 20,
      paddingLeft: TabStyles.Spacing.ExtraLargeSpace - TabStyles.Sizing.BorderWidth,
      paddingRight: TabStyles.Spacing.ExtraLargeSpace - TabStyles.Sizing.BorderWidth,
      paddingBottom: TabStyles.Spacing.DefaultSpace,
      display: 'flex',
      alignItems: 'center',
      lineHeight: 'normal'
    },

    LightweightTitleBarCloseButton: {
      top: 10,
      right: 10,
      position: 'absolute'
    },

    Title: {
      flexGrow: 1,
      fontSize: 'inherit',
      fontWeight: 'inherit',
      margin: 0,
      ...TextEllipsis
    },
  };
};

export class DialogHeader extends React.Component<DialogContentProps, {}> {
  private globalPointerEventCapturer?: GlobalPointerEventCapturer;

  private closeButtonElement: HTMLElement | null;
  private startXPosition: number;
  private startYPosition: number;

  public componentWillUnmount(): void {
    this.removeMouseEventListeners();
  }

  private addMouseEventListeners(): void {
    this.globalPointerEventCapturer!.addListeners();
  }

  private removeMouseEventListeners(): void {
    this.globalPointerEventCapturer!.removeListeners();
  }

  private handleStart = (event: React.MouseEvent<HTMLDivElement> | React.TouchEvent<HTMLDivElement> | MouseEvent | TouchEvent): void => {
    const targetNode = event.target! as Node;
    if (!this.closeButtonElement?.contains(targetNode) && this.props.positioningBehavior === DialogPositioningBehavior.Draggable) {
      event.preventDefault();
      const clientPosition = getClientPosition(event);

      const dialogClientRect = this.props.getDraggableDialogClientRect();
      this.startXPosition = clientPosition.x - dialogClientRect.left;
      this.startYPosition = clientPosition.y - dialogClientRect.top;
      this.addMouseEventListeners();
    }
  }

  /**
   * Moves the dialog container
   */
  private handleMove = (event: MouseEvent | TouchEvent): void => {
    event.preventDefault(); // prevent touch events from firing mouse events
    const clientPosition = getClientPosition(event);
    this.props.setDraggableDialogPosition({
      top: clientPosition.y - this.startYPosition,
      left: clientPosition.x - this.startXPosition
    });
  }

  private handleStop = (event: MouseEvent | TouchEvent): void => {
    event.preventDefault(); // prevent touch events from firing mouse events
    this.removeMouseEventListeners();
  }

  private handleCloseButtonRef = (ref: HTMLDivElement | null): void => {
    this.closeButtonElement = ref;
  }

  private handleCloseButtonClick: React.MouseEventHandler<HTMLElement> = e => {
    e.preventDefault();
    this.props.onCloseRequest?.();
  }

  private handleRef = (ref: HTMLDivElement | null): void => {
    if (this.globalPointerEventCapturer) {
      this.removeMouseEventListeners();
    }
    if (!ref) { return; }
    this.globalPointerEventCapturer = new GlobalPointerEventCapturer(this.handleMove, this.handleStop);

    // React has a very old bug about synthetic `touchstart` handlers working poorly with Chrome -- so we bind it natively here instead
    // See https://github.com/facebook/react/issues/9809
    ref.addEventListener('touchstart', this.handleStart, { passive: false });
  }

  public render(): JSX.Element {
    const styles = getStyles();

    const headerStyle = {
      ...styles.Header,
      ...conditional(this.props.positioningBehavior === DialogPositioningBehavior.Draggable, styles.DraggableHeader)
    };

    let showTitleBar = false;
    let titleBarStyle = {};
    let closeButtonWrapperStyle: React.CSSProperties = {
      display: 'inline-block',
    };
    if (this.props.type === DialogType.NormalWithTitleBar) {
      showTitleBar = true;
      titleBarStyle = {
        ...styles.TitleBar,
        ...conditional(this.props.showCloseButton, styles.TitleBarWithCloseButton)
      };
    } else if (this.props.type === DialogType.LightweightTitleBar) {
      showTitleBar = true;
      titleBarStyle = { ...styles.LightweightTitleBar };
      closeButtonWrapperStyle = { ...styles.LightweightTitleBarCloseButton };
    }

    const closeButtonProps = {
      testId: this.props.testId,
      title: Messages.closeDialog(),
      onClick: this.handleCloseButtonClick,
      ref: this.handleCloseButtonRef
    };

    return (
      <div
        {...EventUtilsOptOut}
        data-tb-test-id={makeTestId(this.props.testId, 'Header')}
        onMouseDown={this.handleStart} // onTouchStart handler is bound natively in handleRef
        ref={this.handleRef}
        style={headerStyle}
      >
        {showTitleBar &&
          <div
            data-tb-test-id={makeTestId(this.props.testId, 'TitleBar')}
            style={titleBarStyle}
          >
            {!!this.props.title &&
              <h2
                children={this.props.title}
                id={this.props.id}
                style={styles.Title}
              />
            }
            {this.props.showCloseButton &&
              <div style={closeButtonWrapperStyle}>
                <DialogCloseButtonWidget {...closeButtonProps} />
              </div>
            }
          </div>
        }
      </div>
    );
  }
}
